import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type RefObject,
} from 'react';
import { createGrantFlow, type GrantFlow } from '@lib/overlay/grantFlow';
import { getOverlayHolderId, subscribeOverlayMutex, type AcquireOptions } from '@lib/overlay/mutex';
import { logger } from '@utils/logger';

export interface OverlayGrant {
  /** True while this instance's request holds the grant: the surface's only open-state. */
  readonly open: boolean;
  /** Idle or settled → requests the grant. Pending or held → nothing. After unmount → nothing. */
  readonly show: () => void;
  /** Held → ends the grant, and the next waiter drains. Pending → withdraws. Idle → nothing. */
  readonly dismiss: (reason?: 'user' | 'programmatic' | 'back-button') => void;
}

/** Requests with an onBackButton wrapper that reads the latest render's handler when the mutex calls it. */
function requestLatest(
  flow: GrantFlow,
  optsRef: RefObject<Pick<AcquireOptions, 'onBackButton'>>,
): void {
  flow.request({
    onBackButton: grant => {
      const handler = optsRef.current.onBackButton;
      if (handler) handler(grant);
      else grant.dismiss('back-button');
    },
  });
}

/**
 * A screen surface's claim on the overlay mutex (overlay-system-design §6.3,
 * "Screen surfaces that keep their own render"). Openers call `show()`; the
 * primitive renders `open` and closes with `dismiss('user')`. Nothing else
 * writes the open-state, so there is nothing to keep in sync.
 *
 * Options are read when they are used, not when `show()` ran: `onBackButton`
 * when back is pressed.
 *
 * Unmount, or a changed `id`, withdraws this instance's claim; a changed id
 * does not re-show. `id` is an entity id, never a route param.
 */
export function useOverlayGrant(
  id: string,
  opts: Pick<AcquireOptions, 'onBackButton'> = {},
): OverlayGrant {
  // One flow per id. A changed id gets a fresh flow here, and the effect below
  // withdraws the old one.
  const [slot, setSlot] = useState(() => ({ id, flow: createGrantFlow(id) }));
  let flow: GrantFlow = slot.flow;
  if (slot.id !== id) {
    const next = { id, flow: createGrantFlow(id) };
    setSlot(next);
    flow = next.flow;
  }

  const optsRef = useRef(opts);
  useLayoutEffect(() => {
    optsRef.current = opts;
  });

  // The mounted flow: a show() that outlives unmount (a mutation settling
  // late, a stale closure) can't start a claim nobody will withdraw. Unmount
  // withdraws rather than dismisses (§6.2 rule 7); when the same flow sets up
  // again (StrictMode, Fast Refresh) it re-requests in the same tick, keeping
  // its place.
  const liveFlowRef = useRef<GrantFlow | null>(null);
  const resumeRef = useRef<GrantFlow | null>(null);
  useLayoutEffect(() => {
    liveFlowRef.current = flow;
    if (resumeRef.current === flow) {
      resumeRef.current = null;
      requestLatest(flow, optsRef);
    }
    return () => {
      liveFlowRef.current = null;
      if (flow.getState() !== 'idle') resumeRef.current = flow;
      flow.withdraw();
    };
  }, [flow]);

  // The mutex notifies synchronously when it grants or ends an entry, so a swap
  // commits on the sync lane (§6.2 rule 3). The flow's own notifications cover
  // the one change the mutex stays silent on: joining an entry that exists.
  const subscribe = useCallback(
    (listener: () => void) => {
      const offMutex = subscribeOverlayMutex(listener);
      const offFlow = flow.subscribe(listener);
      return () => {
        offMutex();
        offFlow();
      };
    },
    [flow],
  );
  const open = useSyncExternalStore(
    subscribe,
    () => flow.getState() !== 'idle' && getOverlayHolderId() === id,
  );

  // Stable per flow, so consumers can list them (or the whole grant) as effect
  // dependencies without re-running the effect on every render.
  const show = useCallback(() => {
    if (liveFlowRef.current !== flow) {
      logger.debug(`[useOverlayGrant] show() for "${id}" after unmount; dropped.`);
      return;
    }
    requestLatest(flow, optsRef);
  }, [flow, id]);

  const dismiss = flow.end;

  return useMemo(() => ({ open, show, dismiss }), [open, show, dismiss]);
}
